import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, Tag, Facebook, Instagram, Share2, MapPin, Star, Heart, Camera } from 'lucide-react';
import { motion } from 'framer-motion';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

const SafariPhotographyGuide = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'The Complete Safari Photography Guide | TravelCord Blog';
  }, []);

  const handleShare = async (platform: string) => {
    const url = window.location.href;
    const title = "The Complete Safari Photography Guide";

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'instagram':
        alert('Follow us on Instagram @travelcord.guide');
        break;
      default:
        if (navigator.share) {
          try {
            await navigator.share({ title, url });
          } catch (error) {
            console.error('Error sharing:', error);
          }
        }
    }
  };

  const photographyTips = [
    {
      title: "Camera Settings",
      tips: [
        "Use fast shutter speeds for wildlife",
        "Keep ISO settings flexible",
        "Shoot in RAW format",
        "Use continuous autofocus"
      ]
    },
    {
      title: "Composition",
      tips: [
        "Follow the rule of thirds",
        "Include environmental context",
        "Capture animal behavior",
        "Consider different angles"
      ]
    },
    {
      title: "Equipment",
      tips: [
        "Bring a telephoto lens",
        "Pack extra batteries",
        "Use a sturdy tripod",
        "Carry lens cleaning supplies"
      ]
    }
  ];

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <img 
            src="https://imgur.com/Rl4EEiV.jpg"
            alt="Safari Photography"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/blog" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Blog
            </Link>
          </div>
          
          <div className="max-w-3xl">
            <div className="flex items-center mb-4 space-x-4">
              <img 
                src="https://imgur.com/G5VWLDc.jpg"
                alt="Robert Kato"
                className="h-12 w-12 rounded-full border-2 border-white"
              />
              <div>
                <h3 className="text-lg font-medium">Robert Kato</h3>
                <p className="text-white/80 text-sm">Safari Guide & Photographer</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="inline-flex items-center bg-ta-green/20 rounded-full px-2.5 py-0.5 text-xs text-ta-green mb-4 backdrop-blur-sm">
                Photography
              </div>
              
              <h1 className="text-4xl font-bold">The Complete Safari Photography Guide</h1>
              
              <div className="flex flex-wrap gap-4 text-sm text-white/80">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  March 10, 2025
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  10 min read
                </div>
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  Uganda National Parks
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="prose max-w-none">
                <p className="lead text-lg text-gray-600">
                  Capturing the perfect wildlife photograph requires more than just good equipment. 
                  This comprehensive guide will help you master the art of safari photography and 
                  create stunning images of Uganda's incredible wildlife.
                </p>

                <h2>Essential Camera Equipment</h2>
                <p>
                  While it's possible to take good photos with any camera, certain equipment will 
                  help you capture better wildlife images:
                </p>
                <ul>
                  <li>DSLR or Mirrorless camera with good autofocus</li>
                  <li>Telephoto lens (minimum 300mm)</li>
                  <li>Fast memory cards</li>
                  <li>Extra batteries and charger</li>
                  <li>Lens cleaning kit</li>
                  <li>Camera rain cover</li>
                </ul>

                <div className="my-8">
                  <img 
                    src="https://imgur.com/WqH4Bly.jpg"
                    alt="Wildlife Photography"
                    className="rounded-lg w-full"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    A lion captured during golden hour in Queen Elizabeth National Park
                  </p>
                </div>

                {photographyTips.map((section, index) => (
                  <div key={index} className="my-8">
                    <h2>{section.title}</h2>
                    <ul>
                      {section.tips.map((tip, tipIndex) => (
                        <li key={tipIndex}>{tip}</li>
                      ))}
                    </ul>
                  </div>
                ))}

                <div className="bg-gray-50 p-6 rounded-lg my-8">
                  <h3 className="text-lg font-semibold mb-4">Pro Tips</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <Camera className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <span>Shoot during golden hour for best lighting</span>
                    </li>
                    <li className="flex items-start">
                      <Camera className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <span>Always be ready - wildlife moments are unpredictable</span>
                    </li>
                    <li className="flex items-start">
                      <Camera className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <span>Focus on the animal's eyes</span>
                    </li>
                    <li className="flex items-start">
                      <Camera className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <span>Tell a story with your images</span>
                    </li>
                  </ul>
                </div>

                <h2>Best Locations for Wildlife Photography</h2>
                <p>
                  Uganda offers numerous excellent locations for wildlife photography:
                </p>
                <ul>
                  <li>Queen Elizabeth National Park - Tree climbing lions</li>
                  <li>Murchison Falls - Diverse wildlife and dramatic landscapes</li>
                  <li>Bwindi Impenetrable Forest - Mountain gorillas</li>
                  <li>Kibale Forest - Chimpanzees and primates</li>
                </ul>

                <h2>Post-Processing Tips</h2>
                <p>
                  While capturing great images in-camera is crucial, post-processing can help enhance 
                  your wildlife photos:
                </p>
                <ul>
                  <li>Adjust exposure and contrast</li>
                  <li>Fine-tune white balance</li>
                  <li>Crop for better composition</li>
                  <li>Remove distracting elements</li>
                  <li>Enhance colors naturally</li>
                </ul>
              </div>

              {/* Tags */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="flex flex-wrap gap-2">
                  <span className="text-sm text-gray-600 mr-2">Tags:</span>
                  {["Photography", "Safari", "Wildlife", "Travel Tips", "Uganda"].map((tag, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-ta-green hover:text-white transition-colors cursor-pointer"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Share Buttons */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-semibold mb-4">Share this article</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Facebook className="h-5 w-5 mr-2" />
                    Facebook
                  </button>
                  <button
                    onClick={() => handleShare('instagram')}
                    className="flex items-center px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
                  >
                    <Instagram className="h-5 w-5 mr-2" />
                    Instagram
                  </button>
                  <button
                    onClick={() => handleShare('native')}
                    className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    <Share2 className="h-5 w-5 mr-2" />
                    Share
                  </button>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Author Card */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center mb-4">
                    <img 
                      src="https://imgur.com/G5VWLDc.jpg"
                      alt="Robert Kato"
                      className="h-16 w-16 rounded-full"
                    />
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold">Robert Kato</h3>
                      <p className="text-gray-600">Safari Guide & Photographer</p>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">
                    Robert combines his passion for wildlife with expert photography skills to help 
                    travelers capture unforgettable moments.
                  </p>
                  <button className="w-full py-2 bg-ta-green text-white rounded-lg hover:bg-ta-green-dark transition-colors">
                    Follow
                  </button>
                </div>

                {/* Table of Contents */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold mb-4">Table of Contents</h3>
                  <nav className="space-y-2">
                    <a href="#equipment" className="block text-gray-600 hover:text-ta-green">Essential Equipment</a>
                    <a href="#camera-settings" className="block text-gray-600 hover:text-ta-green">Camera Settings</a>
                    <a href="#composition" className="block text-gray-600 hover:text-ta-green">Composition</a>
                    <a href="#locations" className="block text-gray-600 hover:text-ta-green">Best Locations</a>
                    <a href="#post-processing" className="block text-gray-600 hover:text-ta-green">Post-Processing</a>
                  </nav>
                </div>

                {/* Related Articles */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold mb-4">Related Articles</h3>
                  <div className="space-y-4">
                    <Link to="/blog/ultimate-guide-gorilla-trekking" className="block group">
                      <div className="flex items-start">
                        <img 
                          src="https://imgur.com/LUalhrs.jpg"
                          alt="Gorilla Trekking Guide"
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-gray-900 group-hover:text-ta-green">
                            Ultimate Guide to Gorilla Trekking
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">8 min read</p>
                        </div>
                      </div>
                    </Link>
                    <Link to="/blog/best-time-visit-uganda" className="block group">
                      <div className="flex items-start">
                        <img 
                          src="https://imgur.com/8u8WjGG.jpg"
                          alt="Best Time to Visit Uganda"
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-gray-900 group-hover:text-ta-green">
                            Best Time to Visit Uganda
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">12 min read</p>
                        </div>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default SafariPhotographyGuide;